import { Color, Spacing } from '@persona-id/design-tokens';
import { EditIcon } from '@persona-id/design-tokens/icons';
import { startCase } from 'lodash/fp';
import { FC, Fragment, useMemo, useState } from 'react';
import styled from 'styled-components';

import MaximizableCodeEditor from 'dashboard/components/MaximizableCodeEditor';
import PlatformSchemaEditor from 'dashboard/components/PlatformSchemaEditor';
import JsonSchemaPreview from 'dashboard/components/WorkflowControlPanel/JsonSchemaPreview';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import { StepAdvancedConfigurationWithErrorHandlingPanel } from 'dashboard/components/WorkflowControlPanel/StepAdvancedConfigurationWithErrorHandlingPanel';
import InputArguments from 'dashboard/components/WorkflowControlPanel/StepConfigAction/InputArguments';
import { ParamsBaseProps } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ParamsBaseProps';
import RetryIntervals from 'dashboard/components/WorkflowControlPanel/StepConfigAction/RetryIntervals';
import RunThisCodeModal from 'dashboard/components/WorkflowControlPanel/StepConfigAction/RunThisCodeModal';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import {
  generateJsonSchemaConfig,
  UsableObjectConfig,
  valueTransformer,
} from 'dashboard/lib/workflows';
import { PlatformObjectJsonSchema } from 'dashboard/lib/workflows/platform-object';
import { jsonSchemaToEvalCodeReturnType } from 'dashboard/lib/workflows/util';
import { Trigger } from 'dashboard/models/trigger';
import { WorkflowError } from 'dashboard/models/workflow';
import {
  WorkflowActionEvaluateCodeReturnType,
  WorkflowActionEvaluateCodeV2,
} from 'dashboard/models/workflow-version';
import { useSchemaForFieldPath } from 'dashboard/redux/selectors/workflow-editor-context';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Paragraph } from 'design-system/components/Text';
import ToggleSlider from 'design-system/components/ToggleSlider';
import { paragraphMixins } from 'design-system/mixins/text';
import { toBase64WithUnicode, toStringFromBase64WithUnicode } from 'lib/base64';
import { FieldSchemaJson, FieldSchemaType } from 'lib/fields';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useIsPersonerd from 'lib/hooks/useIsPersonerd';
import useToggle from 'lib/hooks/useToggle';
import { JsonSchemaCore, JsonSchemaObject, JsonSchemaTypes } from 'lib/json-schema';

export const TYPE_TO_JSON_SCHEMA: Record<WorkflowActionEvaluateCodeReturnType, string> = {
  void: JSON.stringify({ type: 'null' }),
  boolean: JSON.stringify({ type: JsonSchemaTypes.Boolean }),
  number: JSON.stringify({ type: JsonSchemaTypes.Number }),
  string: JSON.stringify({ type: JsonSchemaTypes.String }),
  object: JSON.stringify({ type: JsonSchemaTypes.Object, properties: {} }),
};

const DEFAULT_RETURN_SCHEMA: FieldSchemaJson = {
  type: FieldSchemaType.Json,
  key: 'result',
  label: 'Result',
  config: {
    'json-schema': TYPE_TO_JSON_SCHEMA['void'],
  },
};

const RETURN_TYPES: Record<WorkflowActionEvaluateCodeReturnType, string> = {
  void: 'Nothing (Void)',
  boolean: 'Boolean',
  number: 'Number',
  object: 'Object',
  string: 'String',
};

const INITIAL_CODE = `/*
Define arguments above to be passed into the evaluateCodeStep JavaScript function.
Arguments can be either fields or Persona objects that are accessible within the workflow.

Specify the type of value that your function will return; mismatched return types will cause this step to error.

Persona provides a library to make calls on Persona objects. You can instantiate it like so:
const persona = PersonaClient(env.PERSONA_API_KEY, {"Key-inflection": "<your key-inflection"});

To test your code before publishing, click the "Run" button and pass in stub arguments.

For more information, visit https://help.withpersona.com/
*/
async function evaluateCodeStep(args) {
  // YOUR CODE HERE
}`;

const InlineInput = styled.div`
  width: ${({ theme }) => theme.element.width.md};
`;

const PropertyName = styled.dt`
  ${paragraphMixins.mdBold}

  margin-top: ${Spacing[2]};
`;

const PropertyValue = styled.dd.attrs<{ hasError: boolean }>(({ hasError }) => ({
  'aria-invalid': hasError,
}))<{ hasError: boolean }>`
  ${paragraphMixins.md}
  margin-top: ${Spacing[1]};
  color: ${({ hasError }) => (hasError ? Color.TextDanger : 'inherit')};
  overflow-x: auto;
`;

interface ParamsEvaluateCodeV2Props extends ParamsBaseProps {
  editable: boolean;
  errors: WorkflowError[];
  params: WorkflowActionEvaluateCodeV2['params'];
  trigger?: Trigger;
  usableObjects: UsableObjectConfig[];
  updateActionParams: (newParams: WorkflowActionEvaluateCodeV2['params']) => void;
}

const ParamsEvaluateCodeV2: FC<ParamsEvaluateCodeV2Props> = ({
  step,
  editable,
  errors,
  params,
  trigger,
  usableObjects,
  updateActionParams,
  updateStep,
}) => {
  const schemaForFieldPath = useSchemaForFieldPath();
  const isPersonerd = useIsPersonerd();
  const deserializedCode = useMemo(
    () =>
      params['serialized-code']
        ? toStringFromBase64WithUnicode(params['serialized-code'])
        : INITIAL_CODE,
    [params],
  );

  const returnTypeOptions = Object.entries(RETURN_TYPES).map(([value, label]) => ({
    value,
    label,
  }));
  const [objectEditorIsOpen, setObjectEditorIsOpen] = useState(false);

  const returnJsonSchema = useMemo<JsonSchemaObject<JsonSchemaCore>>(() => {
    return JSON.parse(params['return-schema']['config']['json-schema']);
  }, [params]);
  // Save a copy of the return schema for complex object to avoid erasing the user's work
  // just because they switched return type
  const [cachedReturnJsonObjectSchema, cacheReturnJsonObjectSchema] = useState<JsonSchemaObject<
    JsonSchemaCore | PlatformObjectJsonSchema
  > | null>(null);

  const updateParams = <
    T extends
      | 'return-schema'
      | 'retry-intervals'
      | 'serialized-code'
      | 'input-args'
      | 'timeout'
      | 'nitro',
  >(
    key: T,
    newValue: WorkflowActionEvaluateCodeV2['params'][T],
  ) => {
    updateActionParams({
      ...params,
      [key]: newValue,
    });
  };

  const updateReturnJsonSchema = (newJsonSchemaString: string) => {
    updateParams('return-schema', {
      ...DEFAULT_RETURN_SCHEMA,
      config: {
        'json-schema': newJsonSchemaString,
      },
    });
  };

  const { renderErrorMessageFor, hasErrorFor } = useWorkflowError<
    keyof WorkflowActionEvaluateCodeV2['params'] | `input-args.${string}`
  >(errors);

  const [jsonSchema, getDisplayValue] = useMemo(() => {
    const { jsonSchema, tokensToDisplayValue } = generateJsonSchemaConfig({
      usableObjects: usableObjects,
      useOriginal: true,
    });
    return [jsonSchema, valueTransformer(tokensToDisplayValue).getDisplayValue];
  }, [usableObjects]);

  const presentableInputArgs = useMemo(
    () =>
      Object.entries(params['input-args']).map(([argKey, argValue]) => {
        const matchedObject = usableObjects.find(({ value }) => value === argValue);
        const argType =
          matchedObject?.workflowObjectKey ??
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          schemaForFieldPath(argValue, jsonSchema).schema?.fieldType ??
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          schemaForFieldPath(argValue, jsonSchema).schema?.type ??
          'unknown';

        return {
          name: argKey,
          type: argType,
          label: matchedObject?.label ?? getDisplayValue(argValue) + ` (${startCase(argType)})`,
        };
      }),
    [params, usableObjects, schemaForFieldPath, jsonSchema, getDisplayValue],
  );

  const outputType = useMemo<WorkflowActionEvaluateCodeReturnType>(() => {
    const returnJsonSchemaString = params['return-schema']['config']['json-schema'];
    return jsonSchemaToEvalCodeReturnType(returnJsonSchemaString);
  }, [params]);
  const [showRunThisCodeModal, { enable: runThisCode, disable: backToEditCode }] = useToggle();

  const quickjsEnabled = useFeatureFlag('global:workflow-evaluate-code-with-quickjs');
  const quickjsEnabledForcibly = useFeatureFlag('global:workflow-evaluate-code-with-quickjs-force');

  return (
    <>
      <Spacer height={2} />
      <ParamLabel
        labelText='Return Type'
        buttons={
          outputType === 'object' && editable
            ? [
                <Button
                  id='evaluate-code__edit-return-schema'
                  key='edit-return-schema'
                  width='icon'
                  variant='basic'
                  disabled={!editable || objectEditorIsOpen}
                  aria-label='Edit complex return type'
                  onClick={() => setObjectEditorIsOpen(true)}
                >
                  <EditIcon />
                </Button>,
              ]
            : []
        }
      />

      <Paragraph color='primary' size='sm'>
        {RETURN_TYPES[outputType]}
      </Paragraph>
      {outputType === 'object' && (
        <JsonSchemaPreview
          id='evaluate-code__return-schema-preview'
          jsonSchema={returnJsonSchema['properties'] ?? {}}
          requiredFields={returnJsonSchema['required']}
          onClick={() => setObjectEditorIsOpen(true)}
        />
      )}
      <Spacer height={2} />
      <ParamLabel labelText='Passed Arguments' />
      {presentableInputArgs.length > 0 && (
        <dl role='list'>
          {presentableInputArgs.map(({ name, label }, i) => (
            <Fragment key={i}>
              <PropertyName>{name}</PropertyName>
              <PropertyValue hasError={hasErrorFor(`input-args.${name}`)}>{label}</PropertyValue>
            </Fragment>
          ))}
        </dl>
      )}
      {presentableInputArgs.length === 0 && <Paragraph>Nothing</Paragraph>}
      <Spacer height={2} />
      <ParamLabel labelText='Code' />
      <MaximizableCodeEditor
        maximized
        code={deserializedCode}
        disableDebounce
        language='javascript'
        modalTitle='Code Settings'
        isReadOnly={!editable}
        isOutsideClickDisabled={showRunThisCodeModal}
        outsideExceptionalIdPrefixes={[
          'input-arguments__',
          'run-this-code-modal__',
          'platform-schema-editor__',
          'evaluate-code__edit-return-schema',
        ]}
        onClose={() => {
          // only cache the schema until we close the editor to avoid carrying the state to an unrelated step
          cacheReturnJsonObjectSchema(null);
        }}
        onCodeUpdate={(newCode) => {
          updateParams('serialized-code', toBase64WithUnicode(newCode));
        }}
        renderAboveEditor={() => (
          <>
            <ParamLabel
              htmlFor='params-evaluate-code__return-type'
              labelText='Return Type'
              helpText='Expected type of returning value. This step gets error if the type mismatches.'
            />
            <HStack>
              <InlineInput>
                <Select
                  disabled={!editable}
                  id='params-evaluate-code__return-type'
                  value={outputType}
                  onValueChange={(newReturnType: keyof typeof RETURN_TYPES) => {
                    if (newReturnType === 'object') {
                      updateReturnJsonSchema(
                        cachedReturnJsonObjectSchema
                          ? JSON.stringify(cachedReturnJsonObjectSchema)
                          : TYPE_TO_JSON_SCHEMA[newReturnType],
                      );
                      setObjectEditorIsOpen(true);
                    } else {
                      if (outputType === 'object') {
                        cacheReturnJsonObjectSchema(returnJsonSchema);
                      }
                      updateReturnJsonSchema(TYPE_TO_JSON_SCHEMA[newReturnType]);
                    }
                  }}
                >
                  {returnTypeOptions.map((option) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </InlineInput>
              {outputType === 'object' && (
                <Button
                  id='evaluate-code__edit-return-schema'
                  variant='basic'
                  width='icon'
                  disabled={!editable || objectEditorIsOpen}
                  aria-label='Edit complex return type'
                  onClick={() => setObjectEditorIsOpen(true)}
                >
                  <EditIcon />
                </Button>
              )}
            </HStack>
            <Spacer height={2} />
            <ParamLabel
              htmlFor='params-evaluate-code__input-args-schema'
              labelText='Passed Arguments'
            />
            <InputArguments
              args={params['input-args']}
              editable={editable}
              usableObjects={usableObjects}
              onChange={(newArgs) => {
                updateParams('input-args', newArgs);
              }}
            />
            <Spacer height={2} />
          </>
        )}
        onClickRun={
          editable
            ? (params['serialized-code']?.length ?? 0) > 0
              ? runThisCode
              : false
            : undefined
        }
      />
      {objectEditorIsOpen && (
        <PlatformSchemaEditor
          jsonSchema={cachedReturnJsonObjectSchema || returnJsonSchema}
          panelTitle='Edit schema for return object'
          templateOptionsByObjectKey={{}}
          includePersonaObjects={false}
          disableOutsideClick
          onClosePanel={() => {
            setObjectEditorIsOpen(false);
          }}
          updateJsonSchema={(
            jsonSchema: JsonSchemaObject<JsonSchemaCore | PlatformObjectJsonSchema>,
          ) => {
            cacheReturnJsonObjectSchema(jsonSchema);
            updateReturnJsonSchema(JSON.stringify(jsonSchema));
          }}
        />
      )}
      {renderErrorMessageFor('serialized-code')}
      <Spacer height={4} />
      <StepAdvancedConfigurationWithErrorHandlingPanel
        step={step}
        trigger={trigger}
        updateStep={updateStep}
        disabled={!editable}
      >
        {isPersonerd && (
          <>
            <Spacer height={2} />
            <ParamLabel
              helpText='Only Personerd can adjust the execution timeout. Maximum is 60. Default is 15.'
              htmlFor='params-evaluate-code__timeout'
              labelText='Timeout (Second)'
            />
            <Input
              disabled={!editable}
              hasError={hasErrorFor('timeout')}
              id='params-evaluate-code__timeout'
              max={60}
              min={1}
              type='number'
              value={params['timeout'] ?? ''}
              onChange={(e) => {
                const newTimeout = e.target.value === '' ? null : Number(e.target.value);
                updateParams('timeout', newTimeout);
              }}
            />
            {renderErrorMessageFor('timeout')}
            <Spacer height={2} />
            <ParamLabel
              helpText='Only Personerd can toggle this. If enabled, this code is ran with the brand-new processor which is much performant. Learn more about this on Custom Code Homepage!'
              htmlFor='params-evaluate-code__nitro'
              labelText='Nitro™'
            />
            <ToggleSlider
              titleOn='Nitro is enabled'
              titleOff='Nitro is disabled'
              id='params-evaluate-code__nitro'
              value={!!params['nitro']}
              onChange={(enabled) => updateParams('nitro', enabled)}
            />
            {quickjsEnabledForcibly && (
              <Paragraph>Currently, Nitro™ is forced for this organization in runtime.</Paragraph>
            )}
            {!(quickjsEnabled || quickjsEnabledForcibly) && (
              <Paragraph>
                Currently, Nitro™ is disabled for this organization in runtime.
              </Paragraph>
            )}
          </>
        )}
        <Spacer height={2} />
        <RetryIntervals
          editable={editable}
          retryIntervals={params['retry-intervals']}
          updateRetryIntervals={(newIntervals) => updateParams('retry-intervals', newIntervals)}
        />
      </StepAdvancedConfigurationWithErrorHandlingPanel>
      {showRunThisCodeModal && (
        <RunThisCodeModal
          args={presentableInputArgs}
          returnSchema={params['return-schema']}
          serializedCode={params['serialized-code']}
          nitro={!!params['nitro']}
          onClose={backToEditCode}
        />
      )}
    </>
  );
};

export default ParamsEvaluateCodeV2;
